import { APIErrorTranslations, HTTPErrorTranslations } from "translations/types";

const apiErrors: APIErrorTranslations = {
  ACCOUNT_ALREADY_EXISTS: "An account with that email address already exists.",
  ACTIONKIT_REQUEST_FAILED: "Unable to reach membership database.",
  BAD_REQUEST: "Request failed; verify your input and try again.",
  CREDENTIAL_NOT_FOUND: "That sign-in method could not be found.",
  CURRENT_SESSION_NOT_FOUND:
    "Your current session could not be identified. Sign in again and retry.",
  LAST_SECURE_SIGN_IN_METHOD:
    "Add another secure sign-in method before removing this one.",
  MALFORMED_PROFILE_PAYLOAD:
    "Could not update profile; verify your input and try again.",
  MEMBER_NOT_IN_GOOD_STANDING:
    "Unable to verify DSA membership. Contact staff for help.",
  NOT_LOGGED_IN: "User is not logged in. Refresh the page and try again.",
  RATE_LIMIT_EXCEEDED: "Too many requests. Try again later.",
  REQUEST_ENTITY_TOO_LARGE: "Upload too large for server to handle.",
  RESOURCE_NOT_FOUND: "Could not find requested resource.",
  STRONG_AUTH_REQUIRED:
    "Sign in again with a passkey or password and authenticator app.",
  TASK_ALREADY_COMPLETE: "Task already failed or completed, and cannot be cancelled.",
  UNAUTHORIZED: "User is not allowed to view this resource.",
  USER_NOT_IN_BETA_PROGRAM: "User is not allowed to access myDSA at this time.",
  USER_NOT_VERIFIED:
    "Account is not verified. Check your email for a link and contact staff if help is needed.",
  UNHANDLED_ERROR: "Something unexpected went wrong. Please try again later.",
};

const httpErrors: HTTPErrorTranslations = {
  badRequest: "There was an error in your request. Check your inputs and try again.",
  forbidden: "User is not authorized to access this resource.",
  notFound: "Could not find the requested resource.",
  serverError: "Something unexpected went wrong. Please try again later.",
  conflict: "Resource already exists.",
  preconditionFailed: "Precondition not met.",
  networkError: "Unable to reach the server. Please try again later.",
  unauthorized: "User is not logged in.",
};

const en = {
  translation: {
    profileInfo: {
      account: "Update your name, email, and password using the form below.",
      profile: "Update your DSA profile information using the form below.",
    },
    disclaimer:
      "I verify that this is my email address AND this is the email address associated with my DSA membership.",
    createAccount: "Create your dsaID account",
    hasAccount: "Already have an account? Sign in",
    noAccount: "Don't have an account? Sign up",
    signIn: "Sign in",
    login: {
      redirecting: "Redirecting you to DSA sign in...",
      error: {
        title: "We couldn't finish signing you in",
        body: "Your sign-in session could not be verified. Sign in again to continue.",
        action: "Sign in again",
      },
    },
    signInText: "Use your dsaID account to access myDSA and other DSA services.",
    signUpText:
      "myDSA is a portal where DSA members can view and edit their profile information. To sign up, you must be a member in good standing.",
    signupSuccess:
      "Account created! Check your inbox for a confirmation email. Make sure you check your spam folder if you don't see an email.",
    signup: {
      actions: {
        continue: "Continue",
      },
      betaNotice:
        "MyDSA is currently in limited beta, available to select members in good standing.",
      emailHelp: "Use your DSA email address to continue.",
      emailPlaceholder: "you@example.com",
      page: {
        or: "Or",
        signInLink: "sign in to MyDSA",
        title: "Create your account",
      },
      success: {
        body: "If this email address can be used, you will receive a confirmation email. Check your spam folder if you do not see it.",
        title: "Check your inbox",
      },
    },
    changeEmail:
      "To update your e-mail, click this link. You will be asked to enter and re-verify your new e-mail.",
    changePassword:
      "To update your password, click this link to send a password reset email.",
    account: {
      settings: {
        title: "Email address",
      },
      signInMethods: {
        title: "Sign-in methods",
        description:
          "Review the complete ways you can sign in to dsaID. Passwords are optional when you use a passkey.",
        recommended: "Recommended",
        password: {
          add: "Add password",
          change: "Change password",
        },
        totp: {
          add: "Set up authenticator app",
          addAnother: "Add another authenticator app",
        },
        passkey: {
          title: "Passkey",
          description: "Sign in without a password using your device or security key.",
          add: "Set up passkey",
          addAnother: "Add another passkey",
        },
        strongFallback: {
          title: "Password + authenticator app",
          descriptionActive:
            "Sign in with your password and a code from an authenticator app.",
          descriptionMissingAuthenticator:
            "Set up an authenticator app to complete this strong fallback.",
          descriptionMissingPassword:
            "Add a password to complete this strong fallback.",
          descriptionNotSetUp:
            "Set up both a password and an authenticator app as a strong fallback.",
        },
        emailFallback: {
          title: "Password + email code",
          addPassword: "Add password for password + email code",
          badgeAvailable: "Backup sign-in",
          descriptionAvailable:
            "Use your password and a code sent to your email. This option can’t approve sensitive security changes.",
          descriptionNotSetUp:
            "Add a password to make email-code sign-in available. Email codes can’t approve sensitive security changes.",
        },
        status: {
          active: "Active",
          available: "Available",
          checking: "Checking status…",
          finishSetup: "Finish setup",
          notSetUp: "Not set up",
          unavailable: "Status unavailable",
        },
        unavailable:
          "We couldn’t check your current methods. You can still add or update one.",
        credentials: {
          password: "Password",
          totp: "Authenticator app",
          passkey: "Passkey",
          added: "{{label}} · Added {{date}}",
          remove: "Remove {{label}}",
          removeAction: "Remove",
          removeUnavailable:
            "Add another secure sign-in method before removing this one.",
        },
        removal: {
          cancel: "Keep sign-in method",
          confirm: "Remove sign-in method",
          description:
            "Remove {{credential}} from your account? You won’t be able to use it to sign in again.",
          error: "We couldn’t remove that sign-in method. Please try again.",
          reauthenticate:
            "Your security check expired. Sign in again with a passkey or with your password and authenticator app.",
          signInAgain: "Sign in again",
          success: "{{credential}} was removed from your account.",
          title: "Remove sign-in method?",
        },
      },
      recoveryComplete: {
        title: "You’re back in",
        description:
          "Your replacement sign-in method is ready. We kept your other methods and signed out your existing sessions. Remove any method you lost or don’t recognize.",
      },
      changeEmail: "Change email",
      changePassword: "Change password",
      setupTotp: "Set up an authenticator app",
      setupPasskey: "Set up a passkey or security key",
      email: "Email",
      form: {
        title: "Profile information",
      },
      mailingAddress: "Mailing address",
      mailingPreferences: "Mailing preferences",
      name: "Member name",
      password: "Password",
      phone: "Phone",
      save: "Save profile",
    },
    signUp: "Sign up",
    signOut: "Sign out",
    loading: "Loading",
    save: "Save",
    update: "Update",
    dashboard: {
      actions: {
        update: "Update",
      },
      loading: {
        card: "Loading card",
        welcomeCard: "Loading welcome card",
      },
      unavailable: "Not yet available",
      welcome: {
        greeting: "Welcome back,",
        chapter: "Chapter: {{chapter}}",
        localChapter: "Local chapter",
        chapterTooltip:
          "Need to change your chapter? Update your mailing address in Profile.",
        profilePhotoAlt: "{{name}} profile photo",
      },
      membership: {
        title: "Billing",
        billingName: "Billing name",
        billingAddress: "Billing address",
        memberSinceLabel: "Member since",
        memberSince: "Member since {{date}}",
        duesBannerPrefix: "Visit",
        duesBannerSuffix: "to modify your dues.",
        dues: "Dues",
        cardExpiration: "Card expiration",
        paymentMethod: "Payment method",
        nextPayment: "Next payment",
        actions: {
          renew: "Renew membership",
          sendProof: "Send proof of membership",
          viewDetails: "View Details",
        },
        status: {
          goodStanding: "Member in Good Standing",
          former: "Former member",
          lapsed: "Lapsed",
          merged: "Merged account",
          unknown: "Unknown",
        },
      },
      personalInformation: {
        title: "Member profile",
        name: "Name",
        email: "Email",
        phone: "Phone",
        mailingAddress: "Mailing address",
        missingValue: "Not provided",
      },
      preferences: {
        title: "Preferences",
        mailingPreference: {
          physicalMail: "Send physical mail",
          noPhysicalMail: "Do not send physical mail",
          membershipCardOnly: "Send membership card only",
        },
        magazinePreference: {
          sendDemocraticLeft: "Send Democratic Left",
          noDemocraticLeft: "Do not send Democratic Left",
        },
      },
      profile: {
        title: "Profile",
        description:
          "Help us better understand the multiracial working class by updating your profile questions.",
      },
    },
    forms: {
      required: "required",
      profile: {
        primarySection: "Your info",
        mailingSection: "Mailing",
        demographicsSection: "Demographics",
        basic: {
          title: "Basic information",
          description:
            "Update your preferred name, email address, or reset your password.",
        },
        primaryAddress: {
          title: "Primary address",
          description: "Update your primary contact information.",
        },
        mailingAddress: {
          title: "Mailing address",
          description:
            "Update your mailing address. This address will be used for all primary mail, such as your membership card and copies of Democratic Left.",
        },
        union: {
          title: "Union information",
          description: "Update your union name and local branch.",
        },
      },
      account: {
        firstName: "First name",
        lastName: "Last name",
        updateName: {
          title: "Update Name",
          profileBanner:
            "Visit <1>your profile</1> to edit your pronouns and other information.",
        },
        updateEmail: {
          title: "Update Email Address",
          faqBanner:
            "Visit <1>https://www.dsausa.org/resources/faq/</1> to change your email address.",
        },
      },
      preferences: {
        mailingSection: "Mailing",
        mailingAddress: {
          title: "Mailing address",
          banner:
            "Your mailing address is used to determine your affiliated chapter. Changing your mailing address will change your chapter affiliation within 24 hours.\n\nIf you would like to request an At-Large membership, or otherwise override your chapter assignment, you can request that <1>here</1>.",
        },
      },
      disabled: {
        email: "Email address cannot be edited at this time.",
      },
      labels: {
        email: "Email address",
        firstName: "First name",
        lastName: "Last name",
        chosenFirstName: "Chosen first name",
        chosenLastName: "Chosen last name",
        password: "Password",
        passwordConfirmation: "Password confirmation",
        mailingPreference: {
          title: "Mailing preferences",
          description: "Would you like to receive physical mail from DSA?",
          option0: "Yes",
          option1: "No",
          option2: "Membership card only",
        },
        dlMailingPreference: {
          title: "Democratic Left preferences",
          description:
            "Would you like to receive copies of Democratic Left, the DSA member magazine, by mail?",
          option0: "Yes",
          option1: "No, digital only",
        },
        unionMember: {
          title: "Union member",
          description: "Are you a member of a labor union?",
          option0: "No",
          option1: "Yes",
          option2: "Former union member",
          option3: "Retired union member",
        },
        unionLeadership: {
          title: "Union leadership",
          description: "Are you in leadership at your union?",
          option0: "No",
          option1: "Yes",
        },
        gender: {
          title: "Gender",
          description: "What is your gender?",
          tooltip:
            "DSA collects this information to help understand the makeup of our organization and figure out which communities we need to make more organizing efforts in.",
          option0: "Man",
          option1: "Woman",
          option2: "Non-binary",
          option3: "Gender non-conforming",
          option4: "Other (or unspecified)",
        },
        lgbtq: {
          title: "LGBTQ+",
          description: "Do you identity as LGBTQ+?",
          tooltip:
            "DSA collects this information to help understand the makeup of our organization and figure out which communities we need to make more organizing efforts in.",
          option0: "No",
          option1: "Yes",
        },
        unionName: "Union name",
        unionLocal: "Union local",
        primaryLine1: "Address line 1",
        primaryLine2: "Address line 2",
        primaryCity: "City",
        primaryZip: "Zip code",
        primaryState: "State",
        primaryCountry: "Country",
        mailingLine1: "Address line 1",
        mailingLine2: "Address line 2",
        mailingCity: "City",
        mailingZip: "Zip code",
        mailingState: "State",
        mailingCountry: "Country",
      },
      placeholders: {
        email: "Enter your email address",
        firstName: "Enter your first name",
        lastName: "Enter your last name",
        chosenFirstName: "Enter your preferred first name",
        chosenLastName: "Enter your preferred last name",
        phone: "Enter your phone number",
        password: "Choose a password",
        passwordConfirmation: "Type your password again",
        primaryLine1: "Enter your street address",
        primaryLine2: "Enter your street address",
        primaryCity: "Enter your city",
        primaryZip: "Enter your zip code",
        primaryState: "Select a state",
        primaryCountry: "Select a country",
        mailingLine1: "Enter your street address",
        mailingLine2: "Enter your street address",
        mailingCity: "Enter your city",
        mailingZip: "Enter your zip code",
        mailingState: "Enter your state",
        mailingCountry: "Enter your country",
        unionName: "Enter your union name",
        unionLocal: "Enter your union local",
      },
    },
    countries: {
      US: {
        states: {
          AL: { displayName: "Alabama (AL)" },
          AK: { displayName: "Alaska (AK)" },
          AZ: { displayName: "Arizona (AZ)" },
          AR: { displayName: "Arkansas (AR)" },
          CA: { displayName: "California (CA" },
          CO: { displayName: "Colorado (CO)" },
          CT: { displayName: "Connecticut (CT)" },
          DE: { displayName: "Delaware (DE)" },
          DC: { displayName: "District Of Columbia (DC)" },
          FL: { displayName: "Florida (FL)" },
          GA: { displayName: "Georgia (GA)" },
          HI: { displayName: "Hawaii (HI)" },
          ID: { displayName: "Idaho (ID)" },
          IL: { displayName: "Illinois (IL)" },
          IN: { displayName: "Indiana (IN)" },
          IA: { displayName: "Iowa (IA)" },
          KS: { displayName: "Kansas (KS)" },
          KY: { displayName: "Kentucky (KY)" },
          LA: { displayName: "Louisiana (LA)" },
          ME: { displayName: "Maine (ME)" },
          MD: { displayName: "Maryland (MD)" },
          MA: { displayName: "Massachusetts (MA)" },
          MI: { displayName: "Michigan (MI)" },
          MN: { displayName: "Minnesota (MN)" },
          MS: { displayName: "Mississippi (MS)" },
          MO: { displayName: "Missouri (MO)" },
          MT: { displayName: "Montana (MT)" },
          NE: { displayName: "Nebraska (NE)" },
          NV: { displayName: "Nevada (NV)" },
          NH: { displayName: "New Hampshire (NH)" },
          NJ: { displayName: "New Jersey (NJ)" },
          NM: { displayName: "New Mexico (NM)" },
          NY: { displayName: "New York (NY)" },
          NC: { displayName: "North Carolina (NC)" },
          ND: { displayName: "North Dakota (ND)" },
          OH: { displayName: "Ohio (OH)" },
          OK: { displayName: "Oklahoma (OK)" },
          OR: { displayName: "Oregon (OR)" },
          PA: { displayName: "Pennsylvania (PA)" },
          RI: { displayName: "Rhode Island (RI)" },
          SC: { displayName: "South Carolina (SC)" },
          SD: { displayName: "South Dakota (SD" },
          TN: { displayName: "Tennessee (TN)" },
          TX: { displayName: "Texas (TX)" },
          UT: { displayName: "Utah (UT)" },
          VT: { displayName: "Vermont (VT)" },
          VA: { displayName: "Virginia (VA)" },
          WA: { displayName: "Washington (WA)" },
          WV: { displayName: "West Virginia (WV)" },
          WI: { displayName: "Wisconsin (WI)" },
          WY: { displayName: "Wyoming (WY)" },
          AS: { displayName: "American Samoa (AS)" },
          GU: { displayName: "Guam (GU)" },
          MP: { displayName: "Northern Mariana Islands (MP)" },
          PR: { displayName: "Puerto Rico (PR)" },
          UM: { displayName: "United States Minor Outlying Islands (UM)" },
          VI: { displayName: "Virgin Islands (VI)" },
          AA: { displayName: "Armed Forces Americas (AA)" },
          AP: { displayName: "Armed Forces Pacific (AP)" },
          AE: { displayName: "Armed Forces Others (AE)" },
        },
      },
    },
    errors: {
      fallback: "Something went wrong on our end. Please try again later.",
      forms: {
        input: "{{ value }} is required",
        memberFirstName: "Member first name is required",
        memberLastName: "Member last name is required",
        checkbox: "Must check to continue",
        email: {
          missing: "Email is required",
          invalid: "Invalid email address",
        },
        password: {
          missing: "Password is required",
          missingConfirmation: "Password confirmation is required",
          mismatch: "Passwords do not match",
          length: "Password must be at least {{ count }} characters",
        },
        primaryZip: {
          invalid: "Enter a 5-digit ZIP code or ZIP+4 (e.g. 02139-1234).",
        },
        mailingZip: {
          invalid: "Enter a 5-digit ZIP code or ZIP+4 (e.g. 02139-1234).",
        },
      },
      server: {
        api: apiErrors,
        http: httpErrors,
      },
      profileIneligible:
        "Your profile is not eligible for MyDSA. For more information, see the <1>FAQ</1>.",
    },
    messages: {
      profile: {
        success: "Profile updated!",
      },
      passwordReset: {
        success: "Password reset email sent to {{ email }}!",
      },
      mergedAccount: {
        default:
          "This account has been merged with another membership account. Please use the primary membership account to sign in.",
        withEmail:
          "This account has been merged with another membership account. Please log in using this email address: {{email}}.",
      },
      signup: {
        success:
          "Account created! Check the inbox of {{ email }} for a link to confirm your account.",
      },
      memberDetails: {
        formerMember:
          "You are no longer a member of DSA. To rejoin, see the <1>FAQ</1>.",
        lapsedMember:
          "You are behind on dues. For more information, see the <1>FAQ</1>.",
        member: "You are a member in good standing.",
        nonMemberFallback: "Your member status is not available.",
        joinDate: "Join date",
        verifiedEmail: "Your verified e-mail address",
      },
      memberStatusError: {
        lapsedMember: "You are behind on dues. For more information, see the FAQ.",
        formerMember:
          "Your membership expired. For more information or to rejoin, see the FAQ.",
      },
    },
    nav: {
      home: "Home",
      account: "Account",
      profile: "Member profile",
      faq: "FAQ",
    },
    pages: {
      home: "Welcome to myDSA",
      account: "Member profile",
      membership: "Billing",
      backToHome: "Back to home",
      preferences: "Mailing Preferences",
      profile: "Member profile",
      login: "Sign in with your dsaID",
      error: "Oops! We ran into an error.",
      faq: "Frequently asked questions",
    },
    membershipPage: {
      billingName: "Billing name",
      billingAddress: "Billing address",
      comingSoon:
        "Coming soon: managing your dues directly from myDSA! In the meantime, please fill out the forms linked below to complete your requests.",
      donation: "Donation",
      dues: "Dues",
      cardExpiration: "Card expiration",
      nextPaymentDate: "Next payment date",
      paymentMethod: "Payment method",
      paymentHistory: "Payment history",
      unavailable: "Coming soon",
      noPaymentHistory: "No payments made in the last year.",
      actions: {
        title: "Membership actions",
        updateDues: "Update dues or payment method",
        requestWaiver: "Request a dues waiver",
        cancelDues: "Cancel dues payments",
        requestRefund: "Request a refund",
        requestMembershipCard: "Request a new membership card",
      },
      modal: {
        close: "Close",
        title: "Cancelling due to financial hardship?",
        body: "You can continue to be a member of DSA by requesting a dues waiver for one year.",
        cancelMembership: "Cancel my membership",
        requestWaiver: "Request a dues waiver",
      },
    },
    header: {
      menu: "Account menu",
      menuButton: "Open account menu",
      menuLabel: "Menu",
      menuItems: {
        account: "Member profile",
        home: "Home",
        membership: "Billing",
      },
      title: "myDSA",
    },
    footer: {
      support: "Support",
      languages: {
        en: "English",
        es: "Español",
      },
    },
    icons: {
      book: "Book",
      caret: "Caret",
      checkbox: "Checkbox",
      gift: "Gift",
      globe: "Globe",
      lightbulb: "Lightbulb",
      menu: "Menu",
      phone: "Phone",
      star: "Star",
      user: "User",
      warning: "Warning",
    },
  },
} as const;

export default en;
